import {Injectable, NgModule} from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS, MatNativeDateModule, NativeDateAdapter } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { Platform } from '@angular/cdk/platform';
import { DateFormatPipe } from 'app/pipes/date-format.pipe';
import { PhoenixPipesModule } from 'app/pipes/pipes';
import { TODAY_YEAR } from 'app/app.GlobalSettings';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(utc);
dayjs.extend(customParseFormat);

// extend NativeDateAdapter's format method to specify the date format.
@Injectable()
export class CustomDateAdapter extends NativeDateAdapter {

   dateHelper: DateHelper;

   constructor() {
      super('en-GB' , new Platform(1));
      this.dateHelper = new DateHelper();
   }
   // tslint:disable-next-line:ban-types
   format(date: Date, displayFormat: Object): string {
      if (displayFormat === 'input') {
         if (date){
            const d = dayjs.utc(date).toDate();
            // use our custom date format pipe to ensure that dates are displayed consistently
            return new DateFormatPipe('en-GB').transform(d);
         }
         else{
            return '';
         }
      } else {
         return date ? date.toDateString(): '';
      }
   }

   // process the value into the appropriate date
   parse(value: any): Date | null {
      return this.dateHelper.parse(value);
   }

   // used by the calendar picker when generating date internally from the selected value...
   createDate(year: number, month: number, date: number): Date {
      
      if (month < 0 || month > 11 || date < 1) {
        return null;
      }
  
      const d = super.createDate(year, month, date);
      if (d){
         const result = dayjs.utc(d).toDate(); 
         return (result.getMonth() !== month) ? null : result;
      }
      else{
         return null;
      }
    }
   
   // If required extend other NativeDateAdapter methods.
}

// tslint:disable-next-line: max-classes-per-file
@Injectable()
export class DateHelper{

   // regex for DD/MMM/YYYY
   rx_DDMMMYYYY = new RegExp('^\\d{1,2}\/(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)\/\\d\\d\\d\\d$');

   // regex for DD/MM/YYYY
   rx_DDMMYYYY = new RegExp('^\\d{1,2}\/\\d{1,2}\/\\d{4}$');

   // regex for YYYY-MM-DDT00:00:00 - Note to self: the test converts the  string to lowercase so we include lowercase t in regex :)
   rx_YYYYMMDD = new RegExp('^\\d{4}-\\d{1,2}-\\d{1,2}t.*$');

   // values used when working with relative dates
   today = dayjs.utc(new Date(TODAY_YEAR, 0, 1), undefined, true);
   relativeDateThreshold = dayjs(new Date(TODAY_YEAR - 3, 0, 1), undefined, true).valueOf();

   parse(value: any): Date | null {
      const m = this.parseAsMoment(value);
      if (m){
         if (!(m as dayjs.Dayjs).isValid()){
            if (!this.isRelativeDate(value)){
               const m2 = this.utc(value);
               if (m2){
                  const d = m2.toDate();
                  // use the seconds component of the date as flags for 
                  // 1 = incorrect format 
                  // 2 = invalid input
                  const offset = (m2.isValid()) ? 1 : 2;
                  d.setSeconds(offset);
                  return d;
               }
            }
            else{
               return (m as dayjs.Dayjs).toDate();
            }
         }
         else{
            return (m as dayjs.Dayjs).toDate();
         }
      }
      return null;
   }

   parseAsMoment(value: any): dayjs.Dayjs | boolean{
      if (value && (value !== '0')){
         // if a string then check if is relative date
         if (typeof value === 'string'){
            if (this.isRelativeDate(value)){
               const v = value.toLowerCase().replace('today', '').replace('t', '').trim();
               const multiplier = (v.indexOf('-') > -1) ? -1 : 1;
               const s = v.replace('+', '').replace('-', '').trim();
               if (s || (!v)){
                  const days = (s && (/^[-]?\d+$/.test(s))) ? Number(s) : 0;
                  if (days < 366){
                     return this.utc(new Date(TODAY_YEAR, 0, (days * multiplier) + 1));
                  }
               }
            }

            if (!this.test(value , this.rx_DDMMMYYYY) && (!this.test(value , this.rx_YYYYMMDD)) && (!this.test(value , this.rx_DDMMYYYY))) { 
               return false; 
            }
         }
         else{
            // not a string, check if is a date
            if (this.isDateObject(value)){
               // preserve the seconds
               const seconds = value.getSeconds();
               const m = dayjs.utc().set('year', value.getFullYear()).set('month', value.getMonth()).set('date', value.getDate());
               const d = this.utc(m);
               return dayjs.utc(d, undefined, true).add(seconds , 'seconds');
            }
         }

         // not a relative date, string or date - use dayjs to parse the value
         return this.utc(value);         
      }
      else{
         return null;
      }
   }

   isDateObject(value): boolean{
      return (value && (typeof value.getMonth === 'function'));
   }

   isValidDate(d): boolean{
      if (Object.prototype.toString.call(d) === '[object Date]') {
         // it is a date
         if (isNaN(d.getTime())) {
           // date is not valid
           return false;
         } else {
           // date is valid
           return true;
         }
       } else {
         // not a date
         return false;
       }
   }

   addSeconds(val: dayjs.Dayjs | Date, seconds: number): dayjs.Dayjs | Date{
      if (val){
         if (seconds > 0){
            const d = this.utc(val);
            const m = d.add(seconds , 'seconds');
            return (this.isDateObject(val)) ? m.toDate() : m;
         }
         return val;
      }
      return null;
   }

   utc(val?: string | number | Date | dayjs.Dayjs, format?: string): dayjs.Dayjs{
      let m;
      if (val){
         if (typeof val === 'string'){
            if (format){
              m = dayjs.utc(val, format, true);
            }
            else{
              // test if matches DD/MMM/YYYY
              if (this.test(val , this.rx_DDMMMYYYY)){
                m = dayjs.utc(val, 'DD/MMM/YYYY', true);
              }
              else{
                // not DD/MMM/YYYY - check DD/MM/YYYY as the next most common
                if (this.test(val , this.rx_DDMMYYYY)){
                    m = dayjs.utc(val, 'DD/MM/YYYY', true);
                }
              }
            }
         }
         return (m && m.isValid()) ? m : dayjs.utc(val,undefined,true);
      }
      return null;
   }

   utcDate(val?: string | number | Date | dayjs.Dayjs): Date{
      const m = (val) ? this.utc(val) : dayjs.utc();
      const d = m.toDate();
      const v = dayjs.utc().set('year', d.getFullYear()).set('month', d.getMonth()).set('date', d.getDate());
      return v.toDate();
   }

   getDateAsString(val?: string | number | Date, format?: string): string{
    const m = (val) ? this.utc(val): dayjs.utc();
    return m.format(format || 'DD/MMM/YYYY');
   }

   test(val: string, rx: RegExp): boolean{
      return (val && rx) ? rx.test(val.toLowerCase()) : true;
   }

   isRelativeDate(val: string | dayjs.Dayjs): boolean{
      if (val){
         if (typeof val === 'string'){
            const rx = new RegExp('^t(?:oday)?[-+ ]*\\d{0,3}$');
            return val ? rx.test(val.toLowerCase()) : false;
         }
         else{
            return (val.valueOf() > this.relativeDateThreshold);
         }
      }
      return false;
   }

   relativeToAbsolute(val: string): dayjs.Dayjs{
      if (val){
         const d = this.parse(val);
         const m = dayjs.utc(d, undefined, true);
         if (d && this.isRelativeDate(m)){
            // use getUtcDate to ensure we are using utc dates, otherwise BST can give some unexpected results
            const v = this.utcDate(d);
            const diff = dayjs.utc(v).valueOf() - this.utcDate(this.today).valueOf();
            const currentDate = dayjs.utc(this.utcDate());
            return dayjs.utc(currentDate.valueOf() + diff, undefined, true);
         }
         return m;
       }
      return null;
   }
}

export const MY_DATE_FORMATS = {
    parse: {
      dateInput: {month: 'short', year: 'numeric', day: 'numeric'},
    },
    display: {
       dateInput: 'input',
       monthYearLabel: {year: 'numeric', month: 'short'},
       dateA11yLabel: {year: 'numeric', month: 'long', day: 'numeric'},
       monthYearA11yLabel: {year: 'numeric', month: 'long'},
    },
 };

// tslint:disable-next-line:max-classes-per-file
@NgModule({
    imports: [MatDatepickerModule, MatNativeDateModule, PhoenixPipesModule],
    providers: [
      { provide: DateAdapter, useClass: CustomDateAdapter },
      { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
      DateHelper,
    ],
})
 
 export class DateModule {}
